import React from 'react'
import { CmsPage } from '@pylot-data/fwrdschema'
import { useStoreConfig } from 'config'
import { getFirstNotEmpty } from '@corratech/pylot-utils'
import { getCanonical, getFileExtension } from '../util'
import Head from 'next/head'
import { SEO_ENUMS } from '../enums'
import { Organization } from './Organization'
import { SearchBox } from './SearchBox'
import { DefaultOGImage } from '@components/seo/src/ImagePath'

type Page = SEO_ENUMS
export type CMSPageProps = {
    cmsPage: CmsPage
    pathname: string
    titleOverride?: string
    descriptionOverride?: string
    page?: Page
    asPath: string
}

export const CMSPage = ({
    cmsPage,
    titleOverride,
    descriptionOverride,
    pathname,
    page,
    asPath
}: CMSPageProps): JSX.Element => {
    const config = useStoreConfig()
    const { seo, base } = config
    const ogImage = `${base.url.baseUrl}${DefaultOGImage}`

    const ogImageAlt =
        seo?.default?.openGraph?.images &&
        seo?.default?.openGraph?.images[0]?.alt

    const title = getFirstNotEmpty([
        cmsPage.meta_title,
        titleOverride,
        cmsPage.title,
        seo.default.title
    ])

    const description = getFirstNotEmpty([
        descriptionOverride,
        cmsPage?.meta_description,
        seo.default.description
    ])

    return (
        <>
            <Head>
                <title>{title}</title>
                <meta name="title" content={title} />
                <meta name="keywords" content={String(cmsPage.meta_keywords)} />
                <meta name="description" content={String(description)} />
                <link
                    rel="canonical"
                    href={getCanonical(pathname, base.url.baseUrl)}
                />
                {/* TWITTER */}
                <meta name="twitter:title" content={title} />
                <meta name="twitter:description" content={description} />
                <meta
                    property="twitter:domain"
                    content={seo.default.twitter?.handle}
                />
                <meta
                    property="twitter:url"
                    content={getCanonical(pathname, base.url.baseUrl)}
                />
                <meta
                    name="twitter:card"
                    content={seo.default.twitter?.cardType}
                />
                <meta name="twitter:image" content={ogImage} />
                {/* OPEN GRAPH */}
                <meta
                    property="og:url"
                    content={getCanonical(pathname, base.url.baseUrl)}
                />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                <meta property="og:image" content={ogImage} />
                <meta property="og:image:alt" content={ogImageAlt} />
                <meta
                    property="og:image:type"
                    content={`image/${getFileExtension(ogImage!)}`}
                />

                <meta
                    property="og:site_name"
                    content={seo.default.twitter?.site}
                />
            </Head>
            {page === SEO_ENUMS.HOME && (
                <>
                    <Organization asPath={asPath} />
                    <SearchBox asPath={asPath} />
                </>
            )}
        </>
    )
}
